/**
 * SommelAI Phase 2 Integration Service
 * Orchestrates Intelligent Pairing System and Advanced Recommendation Engine
 *
 * This service provides a unified interface for all Phase 2 AI features,
 * combining pairing intelligence with personalized recommendations.
 */

import { intelligentPairingSystem } from "./intelligentPairingSystem.js";
import { advancedRecommendationEngine } from "./advancedRecommendationEngine.js";

class Phase2IntegrationService {
  constructor() {
    this.integrationCache = new Map();
    this.performanceMetrics = {
      requestCount: 0,
      averageResponseTime: 0,
      successRate: 0,
      featureUsage: {
        pairing: 0,
        recommendation: 0,
        search: 0,
        conversational: 0,
      },
    };
  }

  /**
   * Comprehensive beverage discovery with pairing and recommendations
   * @param {Object} request - Discovery request parameters
   * @returns {Object} Complete discovery response with pairings and recommendations
   */
  async comprehensiveBeverageDiscovery(request) {
    const startTime = Date.now();
    this.performanceMetrics.requestCount++;

    try {
      const {
        inventory,
        foodMenu = [],
        userPreferences = {},
        searchQuery = "",
        context = {},
      } = request;

      // Parallel execution of core services
      const [
        searchResults,
        personalizedRecommendations,
        menuAnalysis,
        contextualPairings,
      ] = await Promise.all([
        // Natural language search if query provided
        searchQuery
          ? advancedRecommendationEngine.naturalLanguageSearch(
              searchQuery,
              inventory,
              context
            )
          : Promise.resolve(null),

        // Personalized recommendations
        userPreferences && Object.keys(userPreferences).length > 0
          ? advancedRecommendationEngine.getPersonalizedRecommendations(
              userPreferences,
              inventory,
              context
            )
          : Promise.resolve(null),

        // Food menu analysis for pairing opportunities
        foodMenu.length > 0
          ? intelligentPairingSystem.analyzeFoodMenu(foodMenu, context)
          : Promise.resolve(null),

        // Contextual pairing suggestions
        context.occasion || context.season
          ? intelligentPairingSystem.getContextualPairings(context, inventory)
          : Promise.resolve(null),
      ]);

      // Generate integrated response
      const response = await this.synthesizeDiscoveryResponse({
        searchResults,
        personalizedRecommendations,
        menuAnalysis,
        contextualPairings,
        inventory,
        foodMenu,
        context,
      });

      // Update performance metrics
      const responseTime = Date.now() - startTime;
      this.updatePerformanceMetrics(responseTime, true);

      return response;
    } catch (error) {
      console.error("Error in comprehensive beverage discovery:", error);
      this.updatePerformanceMetrics(Date.now() - startTime, false);
      return this.fallbackDiscoveryResponse(request);
    }
  }

  /**
   * Smart pairing suggestions with AI-enhanced explanations
   * @param {Object} beverage - Beverage to pair
   * @param {Array} foodMenu - Available food items
   * @param {Object} context - Pairing context
   * @returns {Object} Enhanced pairing suggestions
   */
  async smartPairingAnalysis(beverage, foodMenu, context = {}) {
    const startTime = Date.now();
    this.performanceMetrics.featureUsage.pairing++;

    try {
      // Generate comprehensive pairings
      const [directPairings, menuAnalysis, contextualSuggestions] =
        await Promise.all([
          intelligentPairingSystem.generatePairings(
            beverage,
            foodMenu,
            context
          ),
          foodMenu.length > 0
            ? intelligentPairingSystem.analyzeFoodMenu(foodMenu)
            : Promise.resolve(null),
          intelligentPairingSystem.getContextualPairings(context, [beverage]),
        ]);

      // Get detailed explanations for top pairings
      const topPairings = directPairings.slice(0, 5);
      const explainedPairings = await Promise.all(
        topPairings.map((pairing) =>
          intelligentPairingSystem.explainPairing(
            beverage,
            pairing.dish,
            context
          )
        )
      );

      const response = {
        beverage: {
          name: beverage.name,
          type: beverage.type,
          enhanced: !!beverage.aiEnhanced,
        },
        pairings: {
          direct: directPairings,
          explained: explainedPairings,
          contextual: contextualSuggestions,
        },
        menuAnalysis,
        recommendations: await this.generateAlternativePairings(
          beverage,
          directPairings
        ),
        confidence: this.calculatePairingConfidence(directPairings),
        generatedAt: Date.now(),
      };

      const responseTime = Date.now() - startTime;
      this.updatePerformanceMetrics(responseTime, true);

      return response;
    } catch (error) {
      console.error("Error in smart pairing analysis:", error);
      return this.fallbackPairingAnalysis(beverage, foodMenu);
    }
  }

  /**
   * Conversational beverage assistant
   * @param {Object} conversation - Conversation context and history
   * @returns {Object} AI-powered conversational response
   */
  async conversationalAssistant(conversation) {
    const startTime = Date.now();
    this.performanceMetrics.featureUsage.conversational++;

    try {
      const {
        message,
        history = [],
        inventory,
        userProfile = {},
        context = {},
      } = conversation;

      // Determine conversation intent
      const intent = await this.analyzeConversationIntent(message, history);

      let response;
      switch (intent.type) {
        case "search":
          response = await this.handleSearchConversation(
            message,
            inventory,
            userProfile
          );
          break;
        case "recommendation":
          response = await this.handleRecommendationConversation(
            message,
            inventory,
            userProfile
          );
          break;
        case "pairing":
          response = await this.handlePairingConversation(
            message,
            inventory,
            context
          );
          break;
        case "education":
          response = await this.handleEducationalConversation(
            message,
            inventory
          );
          break;
        default:
          response = await this.handleGeneralConversation(
            message,
            inventory,
            userProfile
          );
      }

      // Add conversation memory and learning
      if (userProfile.userId) {
        advancedRecommendationEngine.learnFromInteraction(userProfile.userId, {
          type: "conversation",
          message,
          intent,
          response,
          timestamp: Date.now(),
        });
      }

      const responseTime = Date.now() - startTime;
      this.updatePerformanceMetrics(responseTime, true);

      return {
        ...response,
        intent,
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error("Error in conversational assistant:", error);
      return this.fallbackConversationalResponse(conversation);
    }
  }

  /**
   * Intelligent menu optimization suggestions
   * @param {Object} menuData - Current menu data
   * @returns {Object} Optimization recommendations
   */
  async menuOptimizationSuggestions(menuData) {
    try {
      const { beverages, foodItems = [], analytics = {} } = menuData;

      // Analyze current menu composition
      const menuAnalysis = await this.analyzeMenuComposition(
        beverages,
        foodItems
      );

      // Generate pairing coverage analysis
      const pairingCoverage = await this.analyzePairingCoverage(
        beverages,
        foodItems
      );

      // Identify gaps and opportunities
      const optimizationOpportunities =
        await this.identifyOptimizationOpportunities(
          menuAnalysis,
          pairingCoverage,
          analytics
        );

      // Generate specific recommendations
      const recommendations = await this.generateMenuRecommendations(
        optimizationOpportunities,
        beverages,
        foodItems
      );

      return {
        currentAnalysis: menuAnalysis,
        pairingCoverage,
        opportunities: optimizationOpportunities,
        recommendations,
        projectedImpact: this.calculateProjectedImpact(recommendations),
        generatedAt: Date.now(),
      };
    } catch (error) {
      console.error("Error in menu optimization:", error);
      return this.fallbackMenuOptimization(menuData);
    }
  }

  /**
   * Seasonal recommendation updates
   * @param {string} season - Current season
   * @param {Array} inventory - Available inventory
   * @param {Object} preferences - User/restaurant preferences
   * @returns {Object} Seasonal optimization suggestions
   */
  async seasonalRecommendationUpdate(season, inventory, preferences = {}) {
    try {
      // Get seasonal context
      const seasonalContext = this.getSeasonalContext(season);

      // Generate seasonal recommendations
      const seasonalRecommendations =
        advancedRecommendationEngine.getOccasionRecommendations(
          `seasonal_${season}`,
          inventory,
          { ...preferences, seasonal: true }
        );

      // Analyze seasonal pairing opportunities
      const seasonalPairings = intelligentPairingSystem.getContextualPairings(
        { season, ...seasonalContext },
        inventory
      );

      // Generate promotional suggestions
      const promotionalSuggestions = await this.generateSeasonalPromotions(
        seasonalRecommendations,
        seasonalPairings,
        season
      );

      return {
        season,
        context: seasonalContext,
        recommendations: seasonalRecommendations,
        pairings: seasonalPairings,
        promotions: promotionalSuggestions,
        implementation: this.generateImplementationPlan(
          seasonalRecommendations
        ),
        generatedAt: Date.now(),
      };
    } catch (error) {
      console.error("Error in seasonal recommendation update:", error);
      return this.fallbackSeasonalRecommendations(season, inventory);
    }
  }

  // Private helper methods

  async synthesizeDiscoveryResponse(data) {
    const {
      searchResults,
      personalizedRecommendations,
      menuAnalysis,
      contextualPairings,
      inventory,
      foodMenu,
      context,
    } = data;

    // Combine and deduplicate recommendations
    const allRecommendations = this.combineRecommendations([
      searchResults?.results || [],
      personalizedRecommendations?.recommendations || [],
      contextualPairings || [],
    ]);

    // Generate smart suggestions based on combined data
    const smartSuggestions = await this.generateSmartSuggestions(
      allRecommendations,
      menuAnalysis,
      context
    );

    return {
      summary: {
        totalBeverages: inventory.length,
        enhancedBeverages: inventory.filter((b) => b.aiEnhanced).length,
        foodItems: foodMenu.length,
        recommendationCount: allRecommendations.length,
      },
      searchResults: searchResults
        ? {
            query: searchResults.query,
            results: searchResults.results.slice(0, 8),
            totalFound: searchResults.totalFound,
          }
        : null,
      personalizedRecommendations: personalizedRecommendations
        ? {
            recommendations: personalizedRecommendations.recommendations.slice(
              0,
              8
            ),
            confidence: personalizedRecommendations.confidence,
          }
        : null,
      menuAnalysis,
      contextualPairings: contextualPairings?.slice(0, 6),
      smartSuggestions,
      nextSteps: this.generateNextSteps(allRecommendations, context),
      generatedAt: Date.now(),
    };
  }

  combineRecommendations(recommendationArrays) {
    const combined = [];
    const seen = new Set();

    for (const array of recommendationArrays) {
      for (const item of array) {
        const beverage = item.beverage || item;
        const key = beverage.id || beverage.name;

        if (!seen.has(key)) {
          seen.add(key);
          combined.push({
            beverage,
            score: item.score || item.confidence || 0.7,
            source: this.identifyRecommendationSource(item),
            reasoning: item.explanation || item.reasoning || "AI recommended",
          });
        }
      }
    }

    return combined.sort((a, b) => b.score - a.score);
  }

  identifyRecommendationSource(item) {
    if (item.explanation?.includes("search")) return "search";
    if (item.reasoning?.includes("preference")) return "personalized";
    if (item.contextAlignment) return "contextual";
    return "general";
  }

  async generateSmartSuggestions(recommendations, menuAnalysis, context) {
    const suggestions = [];

    // Suggest pairing exploration
    if (recommendations.length > 0 && menuAnalysis) {
      suggestions.push({
        type: "pairing_exploration",
        title: "Explore Food Pairings",
        description: `Discover how these beverages pair with your ${menuAnalysis.cuisineStyle} cuisine`,
        action: "explore_pairings",
        priority: "high",
      });
    }

    // Suggest profile building
    if (recommendations.filter((r) => r.source === "personalized").length < 3) {
      suggestions.push({
        type: "profile_building",
        title: "Improve Recommendations",
        description:
          "Tell us more about your preferences for better suggestions",
        action: "build_profile",
        priority: "medium",
      });
    }

    // Suggest seasonal exploration
    if (context.season) {
      suggestions.push({
        type: "seasonal_exploration",
        title: `${context.season} Selections`,
        description: `Discover perfect beverages for ${context.season}`,
        action: "explore_seasonal",
        priority: "medium",
      });
    }

    return suggestions;
  }

  generateNextSteps(recommendations, context) {
    const steps = [];

    if (recommendations.length > 0) {
      steps.push({
        action: "try_recommendation",
        title: "Try a Recommendation",
        description: `Start with our top recommendation: ${recommendations[0]?.beverage?.name}`,
        priority: "high",
      });
    }

    if (context.occasion) {
      steps.push({
        action: "occasion_pairing",
        title: "Perfect Pairing",
        description: `Find the ideal beverage for your ${context.occasion}`,
        priority: "medium",
      });
    }

    steps.push({
      action: "explore_more",
      title: "Explore More",
      description: "Use natural language to search for exactly what you want",
      priority: "low",
    });

    return steps;
  }

  async analyzeConversationIntent(message, history) {
    // Simple intent analysis - could be enhanced with more sophisticated NLP
    const lowerMessage = message.toLowerCase();

    if (
      lowerMessage.includes("recommend") ||
      lowerMessage.includes("suggest")
    ) {
      return { type: "recommendation", confidence: 0.8 };
    }

    if (
      lowerMessage.includes("pair") ||
      lowerMessage.includes("goes with") ||
      lowerMessage.includes("match")
    ) {
      return { type: "pairing", confidence: 0.8 };
    }

    if (
      lowerMessage.includes("search") ||
      lowerMessage.includes("find") ||
      lowerMessage.includes("looking for")
    ) {
      return { type: "search", confidence: 0.7 };
    }

    if (
      lowerMessage.includes("learn") ||
      lowerMessage.includes("tell me") ||
      lowerMessage.includes("what is")
    ) {
      return { type: "education", confidence: 0.7 };
    }

    return { type: "general", confidence: 0.5 };
  }

  async handleSearchConversation(message, inventory, userProfile) {
    const searchResults = advancedRecommendationEngine.naturalLanguageSearch(
      message,
      inventory,
      { userProfile }
    );

    return {
      response: `I found ${searchResults.totalFound} beverages matching "${message}". Here are my top recommendations:`,
      recommendations: searchResults.results.slice(0, 3),
      followUp:
        "Would you like me to explain why these are good matches, or search for something more specific?",
      type: "search_response",
    };
  }

  async handleRecommendationConversation(message, inventory, userProfile) {
    const recommendations =
      advancedRecommendationEngine.getPersonalizedRecommendations(
        userProfile,
        inventory,
        { conversational: true }
      );

    return {
      response:
        "Based on your preferences, I have some excellent recommendations for you:",
      recommendations: recommendations.recommendations.slice(0, 3),
      followUp:
        "Any of these catch your interest? I can tell you more about why they're perfect for you.",
      type: "recommendation_response",
    };
  }

  async handlePairingConversation(message, inventory, context) {
    // Extract potential food mentions from message
    const foodMentions = this.extractFoodMentions(message);

    return {
      response: `For pairing with ${
        foodMentions.length > 0 ? foodMentions.join(" and ") : "your meal"
      }, I recommend:`,
      recommendations: inventory.slice(0, 3), // Simplified for demo
      followUp: "Would you like me to explain why these pairings work so well?",
      type: "pairing_response",
    };
  }

  async handleEducationalConversation(message, inventory) {
    return {
      response:
        "I'd be happy to share some knowledge! Here are some interesting beverages to learn about:",
      recommendations: inventory.filter((b) => b.aiEnhanced).slice(0, 3),
      followUp:
        "What specific aspect would you like to learn more about - tasting notes, production methods, or food pairings?",
      type: "educational_response",
    };
  }

  async handleGeneralConversation(message, inventory, userProfile) {
    const conversationalResponse =
      advancedRecommendationEngine.conversationalRecommendation(
        message,
        inventory,
        userProfile
      );

    return {
      response:
        conversationalResponse.response ||
        "I'm here to help you discover amazing beverages! What are you in the mood for?",
      recommendations:
        conversationalResponse.recommendations || inventory.slice(0, 3),
      followUp:
        "Tell me more about what you're looking for and I can give you better suggestions.",
      type: "general_response",
    };
  }

  extractFoodMentions(message) {
    // Simple food extraction - could be enhanced with proper NLP
    const foodWords = [
      "steak",
      "chicken",
      "fish",
      "salmon",
      "pasta",
      "salad",
      "cheese",
      "chocolate",
      "dessert",
    ];
    return foodWords.filter((food) => message.toLowerCase().includes(food));
  }

  async generateAlternativePairings(beverage, directPairings) {
    // Generate alternative beverages that might pair with similar foods
    const alternatives = [];

    if (directPairings.length > 0) {
      alternatives.push({
        suggestion: "Similar Style",
        description: `Other ${beverage.type}s that would work well with these dishes`,
        reasoning: "Same category compatibility",
      });
    }

    return alternatives;
  }

  calculatePairingConfidence(pairings) {
    if (!pairings.length) return 0;

    const avgConfidence =
      pairings.reduce((sum, p) => sum + (p.confidence || 0.5), 0) /
      pairings.length;
    return Math.round(avgConfidence * 100) / 100;
  }

  async analyzeMenuComposition(beverages, foodItems) {
    const composition = {
      beverageTypes: this.categorizeByType(beverages),
      priceDistribution: this.analyzePriceDistribution(beverages),
      enhancementStatus: this.analyzeEnhancementStatus(beverages),
      varietyScore: this.calculateVarietyScore(beverages),
      foodCategories: this.categorizeByType(foodItems),
    };

    return composition;
  }

  categorizeByType(items) {
    const categories = {};
    items.forEach((item) => {
      const type = item.type || item.category || "other";
      categories[type] = (categories[type] || 0) + 1;
    });
    return categories;
  }

  analyzePriceDistribution(beverages) {
    const prices = beverages
      .map((b) => parseFloat(b.price?.replace(/[^0-9.]/g, "") || 0))
      .filter((p) => p > 0);

    if (prices.length === 0)
      return { min: 0, max: 0, avg: 0, distribution: {} };

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: prices.reduce((sum, p) => sum + p, 0) / prices.length,
      distribution: this.createPriceDistribution(prices),
    };
  }

  createPriceDistribution(prices) {
    const distribution = { budget: 0, moderate: 0, premium: 0, luxury: 0 };

    prices.forEach((price) => {
      if (price < 20) distribution.budget++;
      else if (price < 50) distribution.moderate++;
      else if (price < 100) distribution.premium++;
      else distribution.luxury++;
    });

    return distribution;
  }

  analyzeEnhancementStatus(beverages) {
    const enhanced = beverages.filter((b) => b.aiEnhanced).length;
    return {
      enhanced,
      total: beverages.length,
      percentage: Math.round((enhanced / beverages.length) * 100),
    };
  }

  calculateVarietyScore(beverages) {
    const types = new Set(beverages.map((b) => b.type));
    const regions = new Set(beverages.map((b) => b.region).filter(Boolean));
    const producers = new Set(beverages.map((b) => b.producer).filter(Boolean));

    return {
      typeVariety: types.size,
      regionVariety: regions.size,
      producerVariety: producers.size,
      overallScore: (types.size + regions.size / 5 + producers.size / 10) / 3,
    };
  }

  getSeasonalContext(season) {
    const contexts = {
      spring: {
        characteristics: ["fresh", "light", "floral", "crisp"],
        occasions: ["outdoor dining", "brunch", "celebrations"],
        weather: "mild and pleasant",
      },
      summer: {
        characteristics: ["refreshing", "cooling", "bright", "fruity"],
        occasions: ["patio dining", "barbecue", "festivals"],
        weather: "warm and sunny",
      },
      fall: {
        characteristics: ["warming", "rich", "spiced", "harvest"],
        occasions: ["cozy dinners", "holidays", "comfort food"],
        weather: "cool and crisp",
      },
      winter: {
        characteristics: ["warming", "bold", "comforting", "festive"],
        occasions: ["holiday parties", "intimate dinners", "fireside"],
        weather: "cold and cozy",
      },
    };

    return contexts[season.toLowerCase()] || contexts.spring;
  }

  updatePerformanceMetrics(responseTime, success) {
    this.performanceMetrics.averageResponseTime =
      (this.performanceMetrics.averageResponseTime + responseTime) / 2;

    if (success) {
      this.performanceMetrics.successRate =
        (this.performanceMetrics.successRate *
          (this.performanceMetrics.requestCount - 1) +
          1) /
        this.performanceMetrics.requestCount;
    }
  }

  // Fallback methods

  fallbackDiscoveryResponse(request) {
    return {
      summary: {
        totalBeverages: request.inventory?.length || 0,
        enhancedBeverages: 0,
        foodItems: request.foodMenu?.length || 0,
        recommendationCount: 0,
      },
      searchResults: null,
      personalizedRecommendations: null,
      menuAnalysis: null,
      contextualPairings: [],
      smartSuggestions: [
        {
          type: "system_message",
          title: "Service Temporarily Unavailable",
          description:
            "AI features are currently unavailable, but basic functionality remains",
          priority: "high",
        },
      ],
      nextSteps: [
        {
          action: "retry_later",
          title: "Try Again Later",
          description: "AI services should be available shortly",
          priority: "medium",
        },
      ],
      generatedAt: Date.now(),
      fallback: true,
    };
  }

  fallbackPairingAnalysis(beverage, foodMenu) {
    return {
      beverage: { name: beverage.name, type: beverage.type, enhanced: false },
      pairings: { direct: [], explained: [], contextual: [] },
      menuAnalysis: null,
      recommendations: [],
      confidence: 0.0,
      generatedAt: Date.now(),
      fallback: true,
    };
  }

  fallbackConversationalResponse(conversation) {
    return {
      response:
        "I'm here to help with your beverage questions! However, my AI features are temporarily unavailable.",
      recommendations: conversation.inventory?.slice(0, 3) || [],
      followUp:
        "You can still browse our selection, and I'll be back with full capabilities soon.",
      type: "fallback_response",
      intent: { type: "general", confidence: 0 },
      timestamp: Date.now(),
      fallback: true,
    };
  }

  fallbackMenuOptimization(menuData) {
    return {
      currentAnalysis: { message: "Analysis temporarily unavailable" },
      pairingCoverage: {},
      opportunities: [],
      recommendations: [],
      projectedImpact: { message: "Impact analysis unavailable" },
      generatedAt: Date.now(),
      fallback: true,
    };
  }

  fallbackSeasonalRecommendations(season, inventory) {
    return {
      season,
      context: { characteristics: [], occasions: [], weather: "seasonal" },
      recommendations: inventory.slice(0, 6),
      pairings: [],
      promotions: [],
      implementation: { steps: ["Manual review recommended"] },
      generatedAt: Date.now(),
      fallback: true,
    };
  }

  // Utility methods

  getPerformanceMetrics() {
    return { ...this.performanceMetrics };
  }

  clearCaches() {
    this.integrationCache.clear();
    intelligentPairingSystem.clearCache();
    advancedRecommendationEngine.clearCaches();
  }

  getSystemStatus() {
    return {
      phase2Services: {
        pairingSystem: "operational",
        recommendationEngine: "operational",
        integration: "operational",
      },
      cacheStatus: {
        integration: this.integrationCache.size,
        pairing: intelligentPairingSystem.getCacheStats(),
        recommendation: advancedRecommendationEngine.getCacheStats(),
      },
      performance: this.performanceMetrics,
    };
  }
}

// Export the class for cases where multiple instances are needed
export { Phase2IntegrationService };

// Create and export singleton instance
export const phase2IntegrationService = new Phase2IntegrationService();
export default phase2IntegrationService;
